import { titleFromFileName } from '@/onboarding/pickContent';
import { useDraft, type DraftMedia } from '../state/draft';
import { mergeCreatePicks, readCreatePicks, type CreatePicked } from '../screens/new/createPicks';

/** The draft's files and texts, as the pick functions see them. */
export function pickedIn(d: ReturnType<typeof useDraft.getState>): CreatePicked {
  return {
    media: d.attachedMedia,
    docs: [
      ...d.attachedDocs.map((doc) => ({ ...doc, use: 'page' as const })),
      ...(d.pendingBook ? [{ name: d.pendingBook.fileName, text: d.pendingBook.text, use: 'reader' as const }] : []),
    ],
  };
}

/**
 * Files picked anywhere in the journey — the photo step, or the composer's + (2026-10-04) — read
 * and merged into the draft: photos (HEIC converted, as readCreatePicks does), short texts that go
 * on the page, and a long text for the reader. Merged into the draft as it is AFTER the read, never
 * a copy taken before it (a caption arriving meanwhile must not be wiped).
 *
 * Returns what to say and what was added; captions are the caller's to ask for (they need an
 * account), so a signed-out composer simply has none until later.
 */
export async function readIntoDraft(files: File[]): Promise<{ problems: string[]; addedPhotos: DraftMedia[]; docs: number }> {
  const read = await readCreatePicks(files, pickedIn(useDraft.getState()));
  const now = useDraft.getState();
  const merged = mergeCreatePicks(pickedIn(now), read);
  if (merged.added.length) now.setAttachedMedia(merged.media);
  if (read.docs.length) {
    now.setAttachedDocs(merged.docs.filter((d) => d.use === 'page').map(({ name, text }) => ({ name, text })));
    const reader = merged.docs.find((d) => d.use === 'reader');
    const was = now.pendingBook;
    if (!reader) {
      now.setPendingBook(null);
      now.setAttachedDoc(null);
    } else if (!was || was.fileName !== reader.name || was.text !== reader.text) {
      now.setPendingBook({
        fileName: reader.name,
        text: reader.text,
        title: titleFromFileName(reader.name),
        attribution: was?.attribution ?? '',
        headingPattern: '',
      });
    }
  }
  return {
    problems: [...read.problems, ...merged.problems],
    addedPhotos: merged.added.filter((m) => m.contentType === 'image/jpeg'),
    docs: read.docs.length,
  };
}
